import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { agencyInfo } from '../data/mock';
import { trackWhatsAppClick, trackCTAClick } from '../utils/analytics';

/**
 * HeroVideo — Brand-led cinematic intro.
 * The COUTIN logo (with pulsing yellow play) is the protagonist.
 * Tagline + CTAs follow. No technical jargon ("4K / 120fps / FPV").
 */
const HeroVideo = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.6, 0]);
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const scrollToPortfolio = () => {
    trackCTAClick('Ver Portfólio', 'hero');
    document
      .getElementById('portfolio')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWhatsAppContact = () => {
    trackWhatsAppClick('hero', 'Solicitar Orçamento');
    const message = encodeURIComponent(
      'Olá! Vim pelo site da Coutin e gostaria de um orçamento.'
    );
    window.open(
      `https://wa.me/${agencyInfo.whatsapp}?text=${message}`,
      '_blank'
    );
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black"
      data-testid="hero-section"
    >
      {/* Cinematic ambient background */}
      <motion.div
        style={{ scale: backgroundScale }}
        className="absolute inset-0 z-0"
      >
        {/* Layered radial glows (always-on) */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(78,205,196,0.20)_0%,rgba(0,0,0,0)_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(78,205,196,0.10)_0%,rgba(0,0,0,0)_45%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_70%,rgba(93,221,211,0.08)_0%,rgba(0,0,0,0)_50%)]" />

        {/* Slow drifting central glow */}
        <motion.div
          className="absolute top-1/2 left-1/2 w-[70vw] h-[70vw] rounded-full bg-[#4ECDC4]/12 blur-[140px]"
          style={{ translateX: '-50%', translateY: '-50%' }}
          animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0.85, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Grain texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />

        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_40%,rgba(0,0,0,0.85)_100%)] pointer-events-none" />
      </motion.div>

      {/* Optional background video (drops in once /videos/hero-backstage.mp4 exists) */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none z-0"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      >
        <source src="/videos/hero-backstage.mp4" type="video/mp4" />
      </video>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="container mx-auto px-6 py-24 relative z-10 flex flex-col items-center text-center"
      >
        {/* Overline (SEO local) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center space-x-2 mb-10"
          data-testid="hero-overline"
        >
          <div className="w-12 h-[1px] bg-[#4ECDC4]" />
          <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase font-bold text-[#4ECDC4]">
            Produção · Estratégia · Execução
          </span>
          <div className="w-12 h-[1px] bg-[#4ECDC4]" />
        </motion.div>

        {/* Logo with pulsing play */}
        <motion.div
          style={{ y: logoY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-[560px] mb-14"
          data-testid="hero-brand-logo"
        >
          <img
            src="/images/coutin-logo-clean.png"
            alt="Coutin Creative MKT"
            className="w-full h-auto select-none drop-shadow-[0_0_80px_rgba(93,221,211,0.22)]"
            draggable={false}
          />

          {/* Perfectly round yellow play overlay with heartbeat pulse */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: '51%',
              top: '19.92%',
              transform: 'translate(-50%, -50%)',
              width: '28%',
              aspectRatio: '1 / 1',
            }}
          >
            <motion.div
              className="w-full h-full flex items-center justify-center rounded-full"
              style={{
                background:
                  'radial-gradient(circle at 35% 30%, #7DE9E0 0%, #5DDDD3 45%, #2DB5AB 100%)',
                boxShadow:
                  '0 0 0 1px rgba(93,221,211,0.4), 0 0 60px rgba(93,221,211,0.65), inset 0 0 30px rgba(255,255,255,0.15)',
              }}
              animate={{ scale: [1, 1.06, 1, 1.14, 1] }}
              transition={{
                duration: 1.4,
                times: [0, 0.15, 0.3, 0.5, 1],
                repeat: Infinity,
                ease: 'easeInOut',
                repeatDelay: 0.2,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                className="w-[42%] h-[42%]"
                style={{ marginLeft: '6%' }}
                fill="#0A0A0A"
              >
                <path d="M5 3.5v17l15-8.5z" />
              </svg>
            </motion.div>
          </div>

          {/* Heartbeat rings */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: '51%',
              top: '19.92%',
              transform: 'translate(-50%, -50%)',
              width: '28%',
              aspectRatio: '1 / 1',
            }}
          >
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-[#5DDDD3]"
              animate={{
                scale: [1, 1, 2.4, 2.4],
                opacity: [0, 0.85, 0, 0],
              }}
              transition={{
                duration: 1.6,
                times: [0, 0.3, 0.85, 1],
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
            <motion.div
              className="absolute inset-0 rounded-full border border-[#5DDDD3]/70"
              animate={{
                scale: [1, 1, 3, 3],
                opacity: [0, 0.6, 0, 0],
              }}
              transition={{
                duration: 1.6,
                times: [0, 0.4, 0.95, 1],
                repeat: Infinity,
                ease: 'easeOut',
                delay: 0.15,
              }}
            />
          </div>
        </motion.div>

        {/* Tagline (positioning: PRODUÇÃO + ESTRATÉGIA + EXECUÇÃO) */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="text-3xl md:text-5xl lg:text-6xl tracking-tight font-bold text-[#F5F5F0] mb-6 leading-[1.1] max-w-4xl"
          data-testid="hero-headline"
        >
          Imagem, estratégia e execução
          <br />
          para <span className="text-[#4ECDC4] italic font-light">quem precisa fazer acontecer</span>.
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="text-base md:text-lg text-[#A1A1AA] mb-12 max-w-2xl font-light leading-relaxed"
          data-testid="hero-subheadline"
        >
          Produção audiovisual, marketing digital e operações estratégicas em Foz do Iguaçu
          e na Tríplice Fronteira. Uma década unindo criatividade, tecnologia e execução.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <motion.button
            onClick={handleWhatsAppContact}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="group bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-xl shadow-[#4ECDC4]/30"
            data-testid="hero-primary-cta"
          >
            <span>Solicitar um projeto</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>

          <motion.button
            onClick={scrollToPortfolio}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="group border border-[#4ECDC4]/50 text-[#F5F5F0] hover:bg-[#4ECDC4]/10 font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 backdrop-blur-sm"
            data-testid="hero-secondary-cta"
          >
            <Play className="w-5 h-5" />
            <span>Conheça a Coutin</span>
          </motion.button>
        </motion.div>

        {/* Trust signal — proof without specs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="flex items-center gap-3 text-xs text-[#A1A1AA] uppercase tracking-[0.3em] font-light"
          data-testid="hero-trust-signal"
        >
          <span className="text-[#4ECDC4] font-bold">5,0 ★★★★★</span>
          <span className="w-1 h-1 bg-[#4ECDC4]/50 rounded-full" />
          <span>Avaliação Google</span>
          <span className="w-1 h-1 bg-[#4ECDC4]/50 rounded-full" />
          <span>Desde 2015</span>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden md:flex flex-col items-center space-y-2 text-[#A1A1AA]"
      >
        <span className="text-[10px] uppercase tracking-[0.4em]">Role para explorar</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-[#4ECDC4]/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1 h-3 bg-[#4ECDC4]/60 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroVideo;
